import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import { useAppDataContext } from '../../../contexts/appData';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import { TranslationRecord } from '../../../types/language';
import IslandForm from './IslandForm';
import { disableNavigationGuard } from '../../../hooks/useNavigationGuard';

const CreateIslandPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const { refetchIslandAndPortData } = useAppDataContext();
  const { createIsland, createPort } = useIotsApi();

  const mutation = useMutation({
    mutationFn: ({
      island,
      ports,
    }: {
      island: TranslationRecord;
      ports: TranslationRecord[];
    }) =>
      createIsland({ names: island }).then(id =>
        Promise.allSettled(
          ports.map(port => createPort({ names: port, islandId: id })),
        ).then(settledPromises => {
          // If at least a operation succeeded, it is treated as a success and we'll see the outcome
          const failedOperationsCount = settledPromises.filter(
            operation => operation.status === 'rejected',
          ).length;
          if (failedOperationsCount < settledPromises.length) {
            return { id, completeSuccess: failedOperationsCount === 0 };
          }
          throw new Error('All operations failed');
        }),
      ),
  });
  const { enqueueSnackbar } = useSnackbar();

  return (
    <IslandForm
      initialValue={null}
      isEditingByDefault
      onSubmit={value => {
        mutation.mutate(
          {
            island: value.names,
            ports: value.ports.map(port => port.names),
          },
          {
            onSuccess: ({ id, completeSuccess }) => {
              if (completeSuccess) {
                enqueueSnackbar({
                  variant: 'success',
                  message: t('content.island-and-ports-creation-success'),
                });
              } else {
                enqueueSnackbar({
                  variant: 'warning',
                  message: t(
                    'content.island-and-ports-creation-some-errors-found',
                  ),
                });
              }
              refetchIslandAndPortData();
              void disableNavigationGuard(() =>
                navigate(`/admin/islands/${id}`),
              );
            },
            onError: () => {
              enqueueSnackbar({
                variant: 'error',
                message: t('content.island-and-ports-creation-error'),
              });
              mutation.reset();
            },
          },
        );
      }}
      onCancel={() => void navigate('/admin/islands')}
      isPerformingOperation={mutation.isPending}
      isFetching={false}
    />
  );
};

export default CreateIslandPage;
